// Hook that connects to the Borg SSE stream and builds a streaming message
// from deltas. Falls back gracefully if the stream is unavailable.
// useNamingConvention is disabled for this file via biome.mjs override.

import { useCallback, useEffect, useRef, useState } from "react";

import { Logger } from "~/tools/logger";

import type { BorgStreamEvent, BorgStreamEventMetadata } from "../models/borg-stream-types";
import type { BorgContentPart, BorgMessage } from "../models/borg-types";
import { connectBorgStream } from "../queries/borg-stream";

import {
	type StreamSessionMetrics,
	type StreamTerminationReason,
	createStreamSessionMetrics,
	finalizeStreamSessionMetrics,
	updateStreamSessionMetrics,
} from "./_borg-stream-metrics";
import { type StreamEventReduction, buildMessage, reduceStreamEvent } from "./_borg-stream-state";
import type { ConversationLifecyclePhase } from "./_conversation-lifecycle-types";

interface UseBorgStreamResult {
	streamingMessage: BorgMessage | undefined;
	isStreaming: boolean;
}

const logger = Logger.namespace("ai-assistant-drawer:stream");
const FALLBACK_RENDER_DELAY_MS = 16;

/**
 * Connects to the Borg SSE stream when the conversation is in the "polling" phase.
 * Returns a streaming message assembled from deltas.
 *
 * On disconnect or error, returns undefined — the lifecycle hook falls back to polling.
 */
export function useBorgStream(
	conversationId: string | undefined,
	phase: ConversationLifecyclePhase,
): UseBorgStreamResult {
	const [streamingMessage, setStreamingMessage] = useState<BorgMessage | undefined>(undefined);
	const [isStreaming, setIsStreaming] = useState(false);
	const partsRef = useRef<Array<BorgContentPart>>([]);
	const controllerRef = useRef<AbortController | null>(null);
	const streamConversationIdRef = useRef<string | undefined>(undefined);
	const streamMessageIdRef = useRef<string | undefined>(undefined);
	const lastSequenceRef = useRef<number | undefined>(undefined);
	const reconnectCountRef = useRef(0);
	const metricsRef = useRef<StreamSessionMetrics | null>(null);
	type ScheduledRender =
		| { kind: "raf"; id: ReturnType<typeof globalThis.requestAnimationFrame> }
		| { kind: "timeout"; id: ReturnType<typeof globalThis.setTimeout> };
	const scheduledRenderRef = useRef<ScheduledRender | null>(null);

	const clearScheduledRender = useCallback(() => {
		const scheduled = scheduledRenderRef.current;
		if (scheduled == null) {
			return;
		}
		if (scheduled.kind === "raf") {
			if (typeof globalThis.cancelAnimationFrame === "function") {
				globalThis.cancelAnimationFrame(scheduled.id);
			} else {
				globalThis.clearTimeout(scheduled.id);
			}
		} else {
			globalThis.clearTimeout(scheduled.id);
		}
		scheduledRenderRef.current = null;
	}, []);

	const flushRenderNow = useCallback(() => {
		clearScheduledRender();
		setStreamingMessage((prev) => buildMessage(partsRef.current, prev, streamMessageIdRef.current));
	}, [clearScheduledRender]);

	const scheduleRender = useCallback(() => {
		if (scheduledRenderRef.current != null) {
			return;
		}
		const callback = () => {
			scheduledRenderRef.current = null;
			flushRenderNow();
		};
		if (typeof globalThis.requestAnimationFrame === "function") {
			scheduledRenderRef.current = {
				kind: "raf",
				id: globalThis.requestAnimationFrame(callback),
			};
			return;
		}
		scheduledRenderRef.current = {
			kind: "timeout",
			id: globalThis.setTimeout(callback, FALLBACK_RENDER_DELAY_MS),
		};
	}, [flushRenderNow]);

	const clearState = useCallback(() => {
		clearScheduledRender();
		partsRef.current = [];
		streamMessageIdRef.current = undefined;
		lastSequenceRef.current = undefined;
		setStreamingMessage(undefined);
	}, [clearScheduledRender]);

	const stopStreamingSession = useCallback(
		(reason: StreamTerminationReason) => {
			if (controllerRef.current != null) {
				controllerRef.current.abort();
				controllerRef.current = null;
			}
			metricsRef.current = finalizeStreamSessionMetrics(metricsRef.current, conversationId, reason);
			metricsRef.current = null;
			setIsStreaming(false);
		},
		[conversationId],
	);

	const stopStreamingAfterTerminalEvent = useCallback(
		(reason: "done" | "error") => {
			// Keep streamingMessage visible — it will be cleared when phase
			// transitions away from "polling" (polled data has the saved content by then).
			if (partsRef.current.length > 0) {
				flushRenderNow();
			} else {
				clearScheduledRender();
			}
			stopStreamingSession(reason);
		},
		[clearScheduledRender, flushRenderNow, stopStreamingSession],
	);

	const applyReductionState = useCallback(
		(event: BorgStreamEvent, metadata?: BorgStreamEventMetadata): StreamEventReduction => {
			const reduction = reduceStreamEvent(
				{
					parts: partsRef.current,
					messageId: streamMessageIdRef.current,
					lastSequence: lastSequenceRef.current,
				},
				event,
				metadata,
			);
			if (reduction.isMessageBoundary) {
				// Preserve old content until the first event of the new message arrives,
				// then switch to the new message atomically to avoid handover "silent" periods.
				clearScheduledRender();
			}

			partsRef.current = reduction.parts;
			streamMessageIdRef.current = reduction.messageId;
			lastSequenceRef.current = reduction.lastSequence;
			metricsRef.current = updateStreamSessionMetrics(metricsRef.current, event, metadata);
			return reduction;
		},
		[clearScheduledRender],
	);

	const handleSequenceGap = useCallback(
		(reduction: StreamEventReduction) => {
			logger.warn("stream sequence gap detected, switching to polling fallback", {
				conversationId,
				previousSequence: reduction.previousSequence,
				nextSequence: reduction.nextSequence,
			});
			stopStreamingSession("gap");
		},
		[conversationId, stopStreamingSession],
	);

	const handleReductionAction = useCallback(
		(reduction: StreamEventReduction, event: BorgStreamEvent) => {
			switch (reduction.action) {
				case "update":
					scheduleRender();
					break;
				case "snapshot":
					clearScheduledRender();
					if (reduction.snapshotMessage != null) {
						setStreamingMessage(reduction.snapshotMessage);
					}
					break;
				case "clear":
					clearScheduledRender();
					setStreamingMessage(undefined);
					break;
				case "done":
					stopStreamingAfterTerminalEvent(event.type === "error_event" ? "error" : "done");
					break;
				case "none":
					break;
			}
		},
		[clearScheduledRender, scheduleRender, stopStreamingAfterTerminalEvent],
	);

	const handleEvent = useCallback(
		(event: BorgStreamEvent, metadata?: BorgStreamEventMetadata) => {
			const reduction = applyReductionState(event, metadata);
			if (reduction.hasSequenceGap) {
				handleSequenceGap(reduction);
				return;
			}
			handleReductionAction(reduction, event);
		},
		[applyReductionState, handleReductionAction, handleSequenceGap],
	);

	const startPollingStream = useCallback(
		(targetConversationId: string) => {
			clearState();
			reconnectCountRef.current += 1;
			metricsRef.current = createStreamSessionMetrics(reconnectCountRef.current);

			const controller = connectBorgStream(
				targetConversationId,
				handleEvent,
				() => stopStreamingAfterTerminalEvent("error"),
				() => stopStreamingAfterTerminalEvent("done"),
			);

			controllerRef.current = controller;
			setIsStreaming(true);

			return () => {
				stopStreamingSession("aborted");
				clearScheduledRender();
			};
		},
		[clearScheduledRender, clearState, handleEvent, stopStreamingAfterTerminalEvent, stopStreamingSession],
	);

	useEffect(() => {
		if (streamConversationIdRef.current !== conversationId) {
			streamConversationIdRef.current = conversationId;
			clearState();
		}

		if (conversationId == null) {
			stopStreamingSession("aborted");
			clearState();
			return;
		}

		if (phase === "complete" || phase === "cancelled") {
			// Keep streamingMessage available during completion/cancel handoff so
			// partial streamed content does not disappear before persisted transcript
			// polling catches up or the user continues the conversation.
			stopStreamingSession("aborted");
			return;
		}

		if (phase !== "polling") {
			stopStreamingSession("aborted");
			clearState();
			return;
		}

		return startPollingStream(conversationId);
	}, [clearState, conversationId, phase, startPollingStream, stopStreamingSession]);

	return { streamingMessage, isStreaming };
}
